import { useEffect, useState } from "react";
import { ChevronRightIcon } from "lucide-react";
import { Link, useLocation } from "react-router";

import { Button } from "@ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@ui/collapsible";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@ui/sidebar";

import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { routes } from "@/lib/routes";

// Nested Tools sub-menu grouping the Sadie tools.
export const SadieSidebarMenu = ({
  onNavClick,
}: {
  onNavClick: (destination: MIXPANEL_SCREENS) => void;
}) => {
  /* Misc Hooks */
  const location = useLocation();
  const isSadieRouteActive = routes.isToolsSadieRouteActive(location.pathname);

  /* React Hooks */
  const [isOpen, setIsOpen] = useState(isSadieRouteActive);

  // Open on entering a Sadie route only, so it can still be closed while there.
  useEffect(() => {
    if (isSadieRouteActive) setIsOpen(true);
  }, [isSadieRouteActive]);

  /* Handlers */
  const handleDashboardClick = () => {
    onNavClick(MIXPANEL_SCREENS.SADIE_DASHBOARD);
  };

  const handleSimulatorClick = () => {
    onNavClick(MIXPANEL_SCREENS.SADIE_SIMULATOR);
  };

  const handleVoiceCallsClick = () => {
    onNavClick(MIXPANEL_SCREENS.SADIE_VOICE_CALLS);
  };

  /* Render */
  return (
    <Collapsible className="group/sadie" onOpenChange={setIsOpen} open={isOpen}>
      <SidebarMenuSubItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuSubButton asChild isActive={isSadieRouteActive}>
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start font-normal"
            >
              <span>Sadie</span>
              <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/sadie:rotate-90" />
            </Button>
          </SidebarMenuSubButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                isActive={routes.isToolsSadieDashboardRouteActive(
                  location.pathname,
                )}
              >
                <Link to="tools/sadie-dashboard" onClick={handleDashboardClick}>
                  <span>Dashboard</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                isActive={routes.isToolsSadieSimulatorRouteActive(
                  location.pathname,
                )}
              >
                <Link to="tools/sadie-simulator" onClick={handleSimulatorClick}>
                  <span>Simulator</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                isActive={routes.isToolsSadieVoiceCallsRouteActive(
                  location.pathname,
                )}
              >
                <Link
                  to="tools/sadie-voice-calls"
                  onClick={handleVoiceCallsClick}
                >
                  <span>Voice Calls</span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  );
};
